import type { QueryClient } from '@tanstack/react-query';
import { publicEstimateQueryPrefix } from './approvedEstimateCache';

/**
 * Refresh the public-estimate payload immediately after a successful customer
 * approval. This exists for MONEY CORRECTNESS, not tidiness.
 *
 * `customerApprove` reprices the estimate when the customer changed an optional
 * selection, which recomputes `depositAmount` server-side; the Stripe deposit
 * session is then created from that NEW stored value. The deposit CTA only
 * renders after approval, so without this refresh it would show the
 * pre-approval `depositAmount` and `depositSurcharge` — one total and card fee
 * on screen, a different pair actually charged — defeating the
 * fee-before-submission disclosure the CTA exists to provide. Both values ride
 * the same payload, so one refetch corrects both.
 *
 * Two deliberate choices:
 * - The key is PREFIX-matched (`['public','estimate',id]`, no settlement-token
 *   suffix) so it cannot drift out of sync with the several places the full key
 *   is written.
 * - Failures are swallowed. By the time this runs the approval is already
 *   committed server-side, so a transient GET must not surface to the customer
 *   as "Failed to approve".
 */
export async function refetchPublicEstimate<T extends { estimate: unknown }>(
  queryClient: Pick<QueryClient, 'refetchQueries' | 'getQueriesData'>,
  id: string,
  queryKey: readonly unknown[] = publicEstimateQueryPrefix(id),
): Promise<T | null> {
  try {
    await queryClient.refetchQueries({ queryKey });
    // Hand the refreshed payload back so the caller can re-render the signed
    // PDF from the SERVER's repriced estimate rather than the pre-approval
    // client-side projection. The same prefix also matches the sibling
    // 'images' / 'payment-plan' / 'pdf-download-url' queries, so pick the entry
    // that actually carries an estimate rather than assuming a position.
    const entries = queryClient.getQueriesData<T>({ queryKey });
    for (const [, value] of entries) {
      if (value && typeof value === 'object' && 'estimate' in value) {
        return value;
      }
    }
    return null;
  } catch {
    // Intentionally ignored — see above.
    return null;
  }
}
